import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["buttonStyles", "buttonSize"],
  _excluded2 = ["children", "showChildrenForRequestApproval"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Children, useContext } from 'react';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import styled from 'foundations-theming/utils';
import { fwcColor, fwcBorderRadius } from 'foundations-theming/utils/fwc';
import { useCollaborationSidebarHasUnrecoverableError } from 'collaboration-sidebar/public/hooks/useCollaborationSidebarHasUnrecoverableError';
import { useCollaborationSidebarOpenState } from 'collaboration-sidebar/public/hooks/useCollaborationSidebarOpenState';
import { ApprovalContext } from '../../context/approvalContext';
import { useApprovalConditions } from '../../hooks/useApprovalConditions';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import { useHasApprovalScope } from '../../hooks/useHasApprovalScope';
import { ApprovalOperation } from 'ui-product-approvals-common';
import { ErrorBoundary } from '../error/ErrorBoundary';
import { ApprovalButton } from '../button/ApprovalButton';
import { LegacyRequestChangesCommentButton } from '../button/LegacyRequestChangesCommentButton';
import { useHasLegacyRequestChangesCommentButton } from '../../hooks/useHasLegacyRequestChangesCommentButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAIN_OPERATION_PRIORITY = [ApprovalOperation.REQUEST, ApprovalOperation.APPROVE];
const getButtonTokens = size => {
  switch (size) {
    case 'xs':
    case 'extra-small':
      return {
        iconButtonSize: 'extra-small',
        paddingBlock: '100',
        maxBlockSize: '24px'
      };
    case 'sm':
    case 'small':
      return {
        iconButtonSize: 'extra-small',
        paddingBlock: '200',
        maxBlockSize: '32px'
      };
    case 'md':
    case 'medium':
    case 'default':
    default:
      return {
        iconButtonSize: 'small',
        paddingBlock: '300',
        maxBlockSize: '40px'
      };
  }
};
const ButtonSkeleton = styled.div.withConfig({
  displayName: "ApprovalEditorButtons__ButtonSkeleton"
})(["background-color:", ";border-radius:", ";padding-block:", ";padding-inline:", ";display:inline-flex;inline-size:100px;block-size:", ";align-items:center;justify-content:center;user-select:none;cursor:pointer;max-inline-size:100%;overflow:hidden;box-sizing:border-box;"], ({
  theme
}) => theme.color[fwcColor('fwc-button-fill-primary-default')], ({
  theme
}) => theme.borderRadius[fwcBorderRadius('fwc-button')], ({
  theme,
  $paddingBlock
}) => theme.space[$paddingBlock], ({
  theme
}) => theme.space['300'], ({
  $blockSize
}) => $blockSize);
const ButtonSkeletonText = styled.div.withConfig({
  displayName: "ApprovalEditorButtons__ButtonSkeletonText"
})(["border-radius:", ";background:", ";inline-size:40px;block-size:12px;opacity:0.5;"], ({
  theme
}) => theme.borderRadius['200'], ({
  theme
}) => theme.color[fwcColor('fwc-button-text-primary-default')]);
const DropdownIconButton = buttonProps => /*#__PURE__*/_jsx(IconButton, Object.assign({
  "aria-label": unescapedText('productApprovals.ButtonCollapser.moreActions')
}, buttonProps, {
  children: /*#__PURE__*/_jsx(DownIcon, {})
}));
const ApprovalEditorButtonGroup = ({
  additionalDropdownButtons,
  approvalOperationDisabledReason,
  dropdownProps,
  onViewApprovalClick,
  size = 'medium'
}) => {
  var _MAIN_OPERATION_PRIOR;
  const approval = useApprovalContext();
  const {
    isApprovalActive,
    isCancelled
  } = useApprovalConditions();
  const {
    openApprovals,
    openComments
  } = useCollaborationSidebarOpenState();
  const {
    hasUnrecoverableError
  } = useCollaborationSidebarHasUnrecoverableError();
  const handleViewApprovalClick = onViewApprovalClick !== null && onViewApprovalClick !== void 0 ? onViewApprovalClick : openApprovals;
  const viewApprovalDisabled = onViewApprovalClick ? false : hasUnrecoverableError;
  const hasWritePermission = useHasApprovalScope(approval.meta.approvalType, 'write');
  const {
    availableOperations
  } = approval.meta;
  const hasLegacyRequestChangesCommentButton = useHasLegacyRequestChangesCommentButton({
    hasLegacyCommentDestination: true
  });
  const mainOperation = (_MAIN_OPERATION_PRIOR = MAIN_OPERATION_PRIORITY.find(op => availableOperations.includes(op))) !== null && _MAIN_OPERATION_PRIOR !== void 0 ? _MAIN_OPERATION_PRIOR : null;
  const isViewApprovalMain = !mainOperation && isApprovalActive;
  if (!mainOperation && !isViewApprovalMain) {
    return null;
  }
  const hasDisabledReason = approvalOperationDisabledReason !== undefined && approvalOperationDisabledReason !== null;
  const menuOperations = availableOperations.filter(op => op !== mainOperation);
  const showViewApprovalInMenu = Boolean(mainOperation) && (isApprovalActive || isCancelled);
  const additionalDropdownButtonItems = Children.toArray(additionalDropdownButtons).filter(button => typeof button !== 'boolean').map((button, index) => /*#__PURE__*/_jsx(ListItem, {
    children: button
  }, `additional-dropdown-button-${index}`));
  const hasMenuItems = menuOperations.length > 0 || hasLegacyRequestChangesCommentButton || showViewApprovalInMenu || additionalDropdownButtonItems.length > 0;
  const _ref = dropdownProps !== null && dropdownProps !== void 0 ? dropdownProps : {},
    {
      buttonStyles: dropdownButtonStyles,
      buttonSize: callerDropdownButtonSize
    } = _ref,
    otherDropdownProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    iconButtonSize,
    maxBlockSize,
    paddingBlock
  } = getButtonTokens(size);
  const renderViewApproval = () => /*#__PURE__*/_jsx(Tooltip, {
    disabled: !viewApprovalDisabled,
    placement: "left",
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "productApprovals.ApprovalEditorButtons.approvalsUnavailableTooltip"
    }),
    children: /*#__PURE__*/_jsx(Button, {
      "data-test-id": "editor-approval-view-approval-button",
      disabled: viewApprovalDisabled,
      onClick: handleViewApprovalClick,
      size: size,
      use: "primary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "productApprovals.ApprovalEditorButtons.viewApproval"
      })
    })
  });
  return /*#__PURE__*/_jsxs(ButtonGroup, {
    display: "flex",
    children: [mainOperation ? /*#__PURE__*/_jsx(ApprovalButton, {
      availableOperation: mainOperation,
      disabled: !hasWritePermission || hasDisabledReason,
      disabledReason: approvalOperationDisabledReason,
      size: size
    }) : renderViewApproval(), hasMenuItems && /*#__PURE__*/_jsx(Dropdown, Object.assign({
      "data-test-id": "editor-approval-buttons-dropdown",
      Button: DropdownIconButton,
      buttonSize: callerDropdownButtonSize !== null && callerDropdownButtonSize !== void 0 ? callerDropdownButtonSize : iconButtonSize,
      buttonStyles: Object.assign({
        borderInlineStartColor: 'fill-accent-orange-hover',
        borderInlineStartWidth: '100',
        borderInlineStartStyle: 'solid',
        paddingBlock,
        paddingInline: '100',
        maxBlockSize
      }, dropdownButtonStyles),
      buttonUse: "primary",
      caretRenderer: null
    }, otherDropdownProps, {
      children: /*#__PURE__*/_jsxs(List, {
        children: [hasLegacyRequestChangesCommentButton && /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(LegacyRequestChangesCommentButton, {
            approvalType: approval.meta.approvalType,
            onClickRequestChanges: openComments,
            disabled: hasUnrecoverableError,
            disabledReason: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "productApprovals.ApprovalEditorButtons.approvalsUnavailableTooltip"
            })
          })
        }, "legacy-request-changes"), menuOperations.map(operation => /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(ApprovalButton, {
            availableOperation: operation,
            disabled: !hasWritePermission || hasDisabledReason,
            disabledReason: approvalOperationDisabledReason,
            size: size
          })
        }, `approval-${operation}`)), showViewApprovalInMenu && /*#__PURE__*/_jsx(ListItem, {
          children: renderViewApproval()
        }, "view-approval"), additionalDropdownButtonItems]
      })
    }))]
  });
};
const ApprovalEditorButtonsWithContext = _ref2 => {
  let {
      children,
      showChildrenForRequestApproval = false
    } = _ref2,
    buttonGroupProps = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const approval = useApprovalContext();
  const hasReadPermission = useHasApprovalScope(approval.meta.approvalType, 'read');
  const {
    canUserTakeApprovableAction,
    isFetchComplete,
    isAwaitingApprovableObject,
    isLoading: isApprovalConfigOrDataLoading
  } = useApprovalConditions();
  const hasConfigFetchError = Boolean(approval.meta.httpErrors.hasFetchConfigError);
  const hasCriticalFetchError = hasConfigFetchError || Boolean(approval.meta.httpErrors.fetchData);
  const isApprovalScopeLoading = !hasReadPermission && approval.meta.currentUser === undefined;
  /*
   * WARNING: Be careful changing this as it will likely break downstream ATs.
   * Render children whenever approvals is not positively enabled yet — this
   * includes the window where the config request is still in flight
   * (enabled === null), not just when it has resolved to disabled. Keeping the
   * consumer's own button visible until we know approvals is enabled avoids
   * blocking it behind the approvals config request.
   */
  if (approval.config.enabled !== true && !hasConfigFetchError) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }

  /*
   * While the host app is still fetching/creating the approvable object id there is
   * nothing to act on yet, so once approvals config/data loading has settled we render
   * the consumer's own button instead of the loading skeleton below. Mirrors
   * ApprovalButton / ApprovalSidebarButtons, which treat isAwaitingApprovableObject as
   * "not loading". Without this the skeleton blocks the consumer's button indefinitely
   * for assets whose object id is created lazily (e.g. the Social composer before a
   * post is scheduled).
   */
  if (hasReadPermission && isAwaitingApprovableObject && !isApprovalConfigOrDataLoading) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  if (isApprovalScopeLoading || hasReadPermission && !isFetchComplete) {
    const skeletonTokens = getButtonTokens(buttonGroupProps.size);
    return /*#__PURE__*/_jsx(ButtonSkeleton, {
      "data-test-id": "loading-approval",
      $blockSize: skeletonTokens.maxBlockSize,
      $paddingBlock: skeletonTokens.paddingBlock,
      children: /*#__PURE__*/_jsx(ButtonSkeletonText, {})
    });
  }
  if (!hasReadPermission) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  if (hasCriticalFetchError) {
    return null;
  }
  if (canUserTakeApprovableAction) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  if (showChildrenForRequestApproval && approval.meta.availableOperations.includes(ApprovalOperation.REQUEST)) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/_jsx(ApprovalEditorButtonGroup, Object.assign({}, buttonGroupProps));
};

/**
 * When this component renders `View approval`, it requires a mounted
 * CollaborationSidebarProvider configured for Approvals with ApprovalsContent,
 * plus a rendered AsyncExpandableCollaborationSidebar, or
 * LayoutWithCollaborationSidebar.
 *
 * @remarks
 * Consumer styling requirement: add the import below to the consuming app's
 * `project.sass`. Requesting an approval opens the approval modal's
 * hubspot-prosemirror note editor, which renders unstyled without it.
 *
 * @example
 * ```sass
 * @import hubspot-prosemirror/sass/project
 * ```
 */
export const ApprovalEditorButtons = props => {
  const approval = useContext(ApprovalContext);
  if (approval === undefined) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: props.children
    });
  }
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fingerprint: "ApprovalEditorButtons",
    children: /*#__PURE__*/_jsx(ApprovalEditorButtonsWithContext, Object.assign({}, props))
  });
};